import { useState, useEffect, useMemo } from 'react';
import { useHistory } from 'react-router-dom';
import { useAuth } from '../../../AuthContext';
import useAuditLog, { AUDIT_ACTION } from '../../../hooks/useAuditLog';
import { fetchData } from '../../../apiService';
import LogoLoader from '../../common/LogoLoader';
import SearchableDropdown from '../../common/SearchableDropdown';
import { useSessionValidation } from '../../../utils/sessionValidation';
import { Refresh, GetApp, ArrowBack } from '@mui/icons-material';
import { IconButton, Tooltip, Alert, Snackbar } from '@mui/material';
import { exportStyledExcel } from '../../../utils/excelExport';

const PRIMARY     = '#10586a';
const PRIMARY_MID = '#1a7a90';
const PRIMARY_LT  = '#e0f4f7';

const fmtN  = (n) => (parseFloat(n) || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmtQ  = (n) => (parseFloat(n) || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmt2Date = (date) => {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
};

const iconBtn = {
    border: '1px solid rgba(255,255,255,0.3)', borderRadius: '8px',
    height: 29, width: 29, color: '#fff', flexShrink: 0,
    '&:hover': { background: 'rgba(255,255,255,0.1)' },
    '&.Mui-disabled': { color: 'rgba(255,255,255,0.3)' },
};

const StockBasedReport = () => {
    useSessionValidation();
    const { companyId, branchId, token, companyName, username } = useAuth();
    const { logAudit } = useAuditLog();
    const history = useHistory();

    const today = new Date();
    const [fromDate, setFromDate]   = useState(fmt2Date(new Date(today.getFullYear(), today.getMonth(), 1)));
    const [toDate, setToDate]       = useState(fmt2Date(today));
    const [items, setItems]         = useState([]);
    const [categories, setCategories] = useState([]);
    const [itemId, setItemId]       = useState('0');
    const [categoryId, setCategoryId] = useState('0');
    const [data, setData]           = useState([]);
    const [loading, setLoading]     = useState(false);
    const [error, setError]         = useState('');
    const [fetched, setFetched]     = useState(false);
    const [search, setSearch]       = useState('');

    useEffect(() => {
        if (!companyId || !token) return;
        Promise.allSettled([
            fetchData('ItemApi', `/${companyId}`, token),
            fetchData('CategoryApi', `/${companyId}`, token),
        ]).then(([itemsRes, catsRes]) => {
            setItems(itemsRes.status === 'fulfilled' ? (itemsRes.value?.response_data || itemsRes.value || []) : []);
            setCategories(catsRes.status === 'fulfilled' ? (catsRes.value?.response_data || catsRes.value || []) : []);
        });
    }, [companyId, token]);

    useEffect(() => {
        if (companyId && branchId && token && fromDate && toDate) {
            if (new Date(fromDate) > new Date(toDate)) {
                setData([]);
                setFetched(false);
                setError('From Date cannot be greater than To Date');
                return;
            }
            fetchReport();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [companyId, branchId, token]);

    const fetchReport = async () => {
        if (!fromDate || !toDate) { setError('Please select date range'); return; }
        if (new Date(fromDate) > new Date(toDate)) { setError('From Date cannot be greater than To Date'); return; }
        setLoading(true);
        try {
            const res = await fetchData(
                'ItemProfitabilityApi',
                `/StockBasedDetails/${fromDate}/${toDate}/${companyId}/${branchId}/${itemId || 0}/${categoryId || 0}`,
                token
            );
            const rows = res?.response_data || res || [];
            setData(Array.isArray(rows) ? rows : []);
            setFetched(true);
        } catch (err) {
            console.error(err);
            setError('Failed to fetch stock based report');
        } finally {
            setLoading(false);
        }
    };

    const filtered = useMemo(() => {
        if (!search.trim()) return data;
        const q = search.toLowerCase();
        return data.filter(r =>
            (r.Name || '').toLowerCase().includes(q) ||
            (r.Code || '').toLowerCase().includes(q) ||
            (r.CategoryName || '').toLowerCase().includes(q)
        );
    }, [data, search]);

    const totals = useMemo(() => filtered.reduce((acc, r) => ({
        OpeningQty:      acc.OpeningQty      + (parseFloat(r.OpeningQty)      || 0),
        PurchaseQty:     acc.PurchaseQty     + (parseFloat(r.PurchaseQty)     || 0),
        SaleQty:         acc.SaleQty         + (parseFloat(r.SaleQty)         || 0),
        ClosingQty:      acc.ClosingQty      + (parseFloat(r.ClosingQty)      || 0),
        SalesValue:      acc.SalesValue      + (parseFloat(r.SalesValue)      || 0),
        COGS:            acc.COGS            + (parseFloat(r.COGS)            || 0),
        Profit:          acc.Profit          + (parseFloat(r.Profit)          || 0),
    }), { OpeningQty: 0, PurchaseQty: 0, SaleQty: 0, ClosingQty: 0, SalesValue: 0, COGS: 0, Profit: 0 }), [filtered]);

    const handleExport = async () => {
        logAudit(AUDIT_ACTION.EXPORT, 'StockBasedReport', 0);
        if (!filtered.length) return;
        const headers = ['#', 'Code', 'Item Name', 'Category',
            'Opening', 'Purchase', 'Pur.Return', 'Sale', 'Sale Return',
            'Stock In', 'Stock Out', 'Net Movement', 'Closing',
            'Avg Cost', 'Sales Value', 'COGS', 'Profit'];
        const rows = filtered.map((r, i) => [
            i + 1, r.Code, r.Name, r.CategoryName,
            r.OpeningQty, r.PurchaseQty, r.PurchaseReturnQty, r.SaleQty, r.SaleReturnQty,
            r.StockInQty, r.StockOutQty, r.NetMovementQty, r.ClosingQty,
            r.AvgCost, r.SalesValue, r.COGS, r.Profit,
        ]);

        await exportStyledExcel({
            reportTitle: 'STOCK BASED PROFITABILITY REPORT',
            sheetName: 'Stock Based',
            fileName: `StockBasedReport_${fromDate}_${toDate}.xlsx`,
            companyName,
            username,
            fromDate,
            toDate,
            headers: headers,
            rows,
            totalsRow: ['', '', '', 'Totals',
                fmtQ(totals.OpeningQty), fmtQ(totals.PurchaseQty), '', fmtQ(totals.SaleQty), '',
                '', '', '', fmtQ(totals.ClosingQty),
                '', fmtN(totals.SalesValue), fmtN(totals.COGS), fmtN(totals.Profit)],
            columnWidths: [4, 10, 24, 16, 10, 10, 10, 10, 10, 10, 10, 12, 10, 12, 14, 14, 14],
        });
    };

    const thBase = { padding: '6px 8px', fontWeight: 700, fontSize: 11, whiteSpace: 'nowrap', border: '1px solid #4a9aaa', color: '#fff', background: PRIMARY_MID };
    const tdBase = { padding: '5px 8px', fontSize: 11, whiteSpace: 'nowrap', border: '1px solid #e2e8f0' };

    const profitColor = (p) => {
        const v = parseFloat(p) || 0;
        return v > 0 ? '#059669' : v < 0 ? '#dc2626' : '#374151';
    };

    return (
        <div style={{ minHeight: '100%', background: '#f0f2f5', padding: 12, fontFamily: "'Inter', sans-serif", boxSizing: 'border-box' }}>
            <LogoLoader open={loading} message="Loading stock based report..." />
            <Snackbar open={!!error} autoHideDuration={5000} onClose={() => setError('')} anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
                <Alert severity="error" onClose={() => setError('')} sx={{ width: '100%' }}>{error}</Alert>
            </Snackbar>

            <div style={{ background: '#fff', borderRadius: 10, boxShadow: '0 4px 12px rgba(0,0,0,0.08)', border: '1px solid #e2e8f0', overflow: 'hidden' }}>

                {/* Toolbar */}
                <div className="report-toolbar" style={{ display: 'flex', alignItems: 'center', padding: '6px 12px', background: `linear-gradient(135deg, ${PRIMARY} 0%, ${PRIMARY_MID} 100%)`, gap: 6, flexWrap: 'nowrap', overflowX: 'auto', overflowY: 'hidden', scrollbarWidth: 'none' }}>
                    <Tooltip title="Back" arrow>
                        <IconButton onClick={() => history.push('/ReportsHub')} size="small" sx={iconBtn}>
                            <ArrowBack sx={{ fontSize: 16 }} />
                        </IconButton>
                    </Tooltip>
                    <span style={{ fontSize: 13, fontWeight: 700, color: '#fff', whiteSpace: 'nowrap', flexShrink: 0 }}>
                        Stock Based
                        <span style={{ fontSize: 11, fontWeight: 400, opacity: 0.8, display: 'block', lineHeight: 1.2 }}>Item Profitability</span>
                    </span>
                    <div style={{ flex: 1, minWidth: 4 }} />
                    {/* Category filter */}
                    <SearchableDropdown
                        value={categoryId}
                        onChange={v => setCategoryId(v)}
                        options={[{ value: '0', label: 'All Categories' }, ...categories.map(c => ({ value: String(c.Id), label: c.Name }))]}
                        placeholder="All Categories"
                        minWidth={160}
                        primaryColor={PRIMARY}
                    />
                    {/* Item filter */}
                    <SearchableDropdown
                        value={itemId}
                        onChange={v => setItemId(v)}
                        options={[{ value: '0', label: 'All Items' }, ...items.map(it => ({ value: String(it.Id), label: `${it.Code} - ${it.Name}` }))]}
                        placeholder="All Items"
                        minWidth={180}
                        primaryColor={PRIMARY}
                    />
                    {/* Search */}
                    <input placeholder="Search item / category..."
                        value={search} onChange={e => setSearch(e.target.value)}
                        style={{ height: 29, border: '1px solid rgba(255,255,255,0.3)', borderRadius: 8, padding: '0 10px', fontSize: 12, backgroundColor: 'rgba(255,255,255,0.1)', color: '#fff', colorScheme: 'dark', flexShrink: 0, minWidth: 150, outline: 'none' }} />
                    <input type="date" value={fromDate} onChange={e => setFromDate(e.target.value)}
                        style={{ height: 29, border: '1px solid rgba(255,255,255,0.3)', borderRadius: 8, padding: '0 8px', fontSize: 12, backgroundColor: 'rgba(255,255,255,0.1)', color: '#fff', colorScheme: 'dark', flexShrink: 0 }} />
                    <input type="date" value={toDate} onChange={e => setToDate(e.target.value)}
                        style={{ height: 29, border: '1px solid rgba(255,255,255,0.3)', borderRadius: 8, padding: '0 8px', fontSize: 12, backgroundColor: 'rgba(255,255,255,0.1)', color: '#fff', colorScheme: 'dark', flexShrink: 0 }} />
                    <Tooltip title="Refresh" arrow>
                        <IconButton onClick={fetchReport} disabled={loading} size="small" sx={iconBtn}>
                            <Refresh sx={{ fontSize: 16 }} />
                        </IconButton>
                    </Tooltip>
                    <Tooltip title="Export Excel" arrow>
                        <IconButton onClick={handleExport} disabled={!filtered.length} size="small" sx={iconBtn}>
                            <GetApp sx={{ fontSize: 16 }} />
                        </IconButton>
                    </Tooltip>
                </div>

                {/* Summary bar */}
                {fetched && filtered.length > 0 && (
                    <div style={{ display: 'flex', gap: 8, padding: '6px 14px', borderBottom: '1px solid #f1f5f9', flexWrap: 'wrap' }}>
                        {[
                            { label: 'Items', value: filtered.length, isCount: true },
                            { label: 'Sales Value', value: fmtN(totals.SalesValue), color: PRIMARY },
                            { label: 'COGS', value: fmtN(totals.COGS), color: '#d97706' },
                            { label: 'Profit', value: fmtN(totals.Profit), color: totals.Profit >= 0 ? '#059669' : '#dc2626' },
                            { label: 'Closing Qty', value: fmtQ(totals.ClosingQty), color: '#374151' },
                        ].map(s => (
                            <div key={s.label} style={{ background: PRIMARY_LT, borderRadius: 8, padding: '4px 12px', display: 'flex', flexDirection: 'column', alignItems: 'center', minWidth: 100 }}>
                                <span style={{ fontSize: 10, color: '#64748b', fontWeight: 600 }}>{s.label}</span>
                                <span style={{ fontSize: 12, color: s.color || PRIMARY, fontWeight: 700 }}>{s.isCount ? s.value : s.value}</span>
                            </div>
                        ))}
                    </div>
                )}

                {/* Meta */}
                {fetched && (
                    <div style={{ padding: '4px 14px', fontSize: 11, color: '#64748b', borderBottom: '1px solid #f1f5f9' }}>
                        {fromDate} to {toDate} &nbsp;|&nbsp; {filtered.length} items
                        {search && ` (filtered from ${data.length})`}
                        &nbsp;|&nbsp; Profit: <strong style={{ color: totals.Profit >= 0 ? '#059669' : '#dc2626' }}>{fmtN(totals.Profit)}</strong>
                    </div>
                )}

                {/* Table */}
                <div className="report-table-scroll" style={{ overflow: 'auto', overflowY: 'scroll', maxHeight: 'calc(100vh - 190px)' }}>
                    <table style={{ borderCollapse: 'collapse', fontSize: 11, minWidth: 1100, width: '100%' }}>
                        <thead style={{ position: 'sticky', top: 0, zIndex: 3 }}>
                            <tr>
                                <th style={{ ...thBase, textAlign: 'center', width: 36 }}>#</th>
                                <th style={{ ...thBase, textAlign: 'left' }}>Code</th>
                                <th style={{ ...thBase, textAlign: 'left', minWidth: 180 }}>Item Name</th>
                                <th style={{ ...thBase, textAlign: 'left' }}>Category</th>
                                <th style={{ ...thBase, textAlign: 'right' }}>Opening</th>
                                <th style={{ ...thBase, textAlign: 'right' }}>Purchase</th>
                                <th style={{ ...thBase, textAlign: 'right' }}>Pur.Ret</th>
                                <th style={{ ...thBase, textAlign: 'right' }}>Sale</th>
                                <th style={{ ...thBase, textAlign: 'right' }}>Sale Ret</th>
                                <th style={{ ...thBase, textAlign: 'right' }}>Stock In</th>
                                <th style={{ ...thBase, textAlign: 'right' }}>Stock Out</th>
                                <th style={{ ...thBase, textAlign: 'right' }}>Net Mov.</th>
                                <th style={{ ...thBase, textAlign: 'right' }}>Closing</th>
                                <th style={{ ...thBase, textAlign: 'right' }}>Avg Cost</th>
                                <th style={{ ...thBase, textAlign: 'right' }}>Sales Value</th>
                                <th style={{ ...thBase, textAlign: 'right' }}>COGS</th>
                                <th style={{ ...thBase, textAlign: 'right', background: '#0a4a5a' }}>Profit</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filtered.length === 0 ? (
                                <tr>
                                    <td colSpan={17} style={{ textAlign: 'center', padding: 32, color: '#94a3b8', fontSize: 13 }}>
                                        {fetched ? 'No records found' : 'Select date range and click Refresh'}
                                    </td>
                                </tr>
                            ) : filtered.map((r, i) => {
                                const profit = parseFloat(r.Profit) || 0;
                                return (
                                    <tr key={`${r.Id}-${i}`} style={{ background: i % 2 === 0 ? '#fff' : PRIMARY_LT }}>
                                        <td style={{ ...tdBase, textAlign: 'center', color: '#94a3b8' }}>{i + 1}</td>
                                        <td style={{ ...tdBase, color: '#64748b', fontSize: 10 }}>{r.Code || '-'}</td>
                                        <td style={{ ...tdBase, fontWeight: 600, color: PRIMARY }}>{r.Name || '-'}</td>
                                        <td style={{ ...tdBase, color: '#64748b' }}>{r.CategoryName || '-'}</td>
                                        <td style={{ ...tdBase, textAlign: 'right' }}>{fmtQ(r.OpeningQty)}</td>
                                        <td style={{ ...tdBase, textAlign: 'right', color: '#059669' }}>{fmtQ(r.PurchaseQty)}</td>
                                        <td style={{ ...tdBase, textAlign: 'right', color: '#dc2626' }}>{fmtQ(r.PurchaseReturnQty)}</td>
                                        <td style={{ ...tdBase, textAlign: 'right', color: '#0277bd' }}>{fmtQ(r.SaleQty)}</td>
                                        <td style={{ ...tdBase, textAlign: 'right', color: '#dc2626' }}>{fmtQ(r.SaleReturnQty)}</td>
                                        <td style={{ ...tdBase, textAlign: 'right' }}>{fmtQ(r.StockInQty)}</td>
                                        <td style={{ ...tdBase, textAlign: 'right' }}>{fmtQ(r.StockOutQty)}</td>
                                        <td style={{ ...tdBase, textAlign: 'right', fontWeight: 600 }}>{fmtQ(r.NetMovementQty)}</td>
                                        <td style={{ ...tdBase, textAlign: 'right', fontWeight: 700, color: PRIMARY }}>{fmtQ(r.ClosingQty)}</td>
                                        <td style={{ ...tdBase, textAlign: 'right' }}>{fmtN(r.AvgCost)}</td>
                                        <td style={{ ...tdBase, textAlign: 'right', fontWeight: 600 }}>{fmtN(r.SalesValue)}</td>
                                        <td style={{ ...tdBase, textAlign: 'right', color: '#d97706' }}>{fmtN(r.COGS)}</td>
                                        <td style={{ ...tdBase, textAlign: 'right', fontWeight: 700, color: profitColor(r.Profit), background: profit > 0 ? '#f0fdf4' : profit < 0 ? '#fff5f5' : 'transparent' }}>{fmtN(r.Profit)}</td>
                                    </tr>
                                );
                            })}
                        </tbody>
                        {filtered.length > 0 && (
                            <tfoot>
                                <tr style={{ background: PRIMARY_LT, fontWeight: 700 }}>
                                    <td colSpan={4} style={{ ...tdBase, fontWeight: 700, color: PRIMARY, textAlign: 'right' }}>Totals</td>
                                    <td style={{ ...tdBase, textAlign: 'right', color: PRIMARY, fontWeight: 700 }}>{fmtQ(totals.OpeningQty)}</td>
                                    <td style={{ ...tdBase, textAlign: 'right', color: '#059669', fontWeight: 700 }}>{fmtQ(totals.PurchaseQty)}</td>
                                    <td style={{ ...tdBase }} />
                                    <td style={{ ...tdBase, textAlign: 'right', color: '#0277bd', fontWeight: 700 }}>{fmtQ(totals.SaleQty)}</td>
                                    <td colSpan={4} style={{ ...tdBase }} />
                                    <td style={{ ...tdBase, textAlign: 'right', color: PRIMARY, fontWeight: 700 }}>{fmtQ(totals.ClosingQty)}</td>
                                    <td style={{ ...tdBase }} />
                                    <td style={{ ...tdBase, textAlign: 'right', fontWeight: 700, color: PRIMARY }}>{fmtN(totals.SalesValue)}</td>
                                    <td style={{ ...tdBase, textAlign: 'right', fontWeight: 700, color: '#d97706' }}>{fmtN(totals.COGS)}</td>
                                    <td style={{ ...tdBase, textAlign: 'right', fontWeight: 700, color: profitColor(totals.Profit), background: totals.Profit >= 0 ? '#dcfce7' : '#fee2e2' }}>{fmtN(totals.Profit)}</td>
                                </tr>
                            </tfoot>
                        )}
                    </table>
                </div>
            </div>
        </div>
    );
};

export default StockBasedReport;
